import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { api } from '../api'
import ExportButton from '../components/ExportButton'
import LastUpdated from '../components/LastUpdated'

const CYCLE_START = new Date('2026-03-09T00:00:00')
const TOTAL_WEEKS = 9
const WEEK_LABELS = ['W0 Planning', 'W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8', 'Unscheduled']

function getWeekDates(weekIndex) {
  const start = new Date(CYCLE_START)
  start.setDate(start.getDate() + weekIndex * 7)
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  return { start, end }
}

function getCurrentWeek() {
  const now = new Date()
  const diff = now - CYCLE_START
  if (diff < 0) return -1
  return Math.min(Math.floor(diff / (7 * 24 * 60 * 60 * 1000)), TOTAL_WEEKS - 1)
}

function formatDate(d) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function isInWeekRange(updatedAt, weekIndex) {
  if (!updatedAt) return false
  const { start, end } = getWeekDates(weekIndex)
  const endOfWeek = new Date(end)
  endOfWeek.setHours(23, 59, 59, 999)
  const t = new Date(updatedAt)
  return t >= start && t <= endOfWeek
}

const statusEmoji = {
  'Done': '✅', 'Dev': '🔵', 'RFC': '🟡', 'External Flight': '🟢', 'Beta Flight': '🟢',
  'Not started': '⚪', 'Cut': '❌', 'TBD': '⚪', 'GA': '✅',
}

const RAG_OPTIONS = [
  { value: '', label: '— No Status' },
  { value: 'green', label: '🟢 Green — On Track' },
  { value: 'yellow', label: '🟡 Yellow — At Risk' },
  { value: 'red', label: '🔴 Red — Off Track' },
]

const RAG_COLORS = {
  green: { bg: '#E8F5E9', border: '#4CAF50', dot: '🟢' },
  yellow: { bg: '#FFF8E1', border: '#FFC107', dot: '🟡' },
  red: { bg: '#FFEBEE', border: '#F44336', dot: '🔴' },
}

function WeeklySummaryCard({ week, summary, onSave, isEditable, isW0 }) {
  const [editing, setEditing] = useState(false)
  const [rag, setRag] = useState(summary?.rag || '')
  const [highlights, setHighlights] = useState(summary?.highlights || '')
  const [lowlights, setLowlights] = useState(summary?.lowlights || '')
  // W0 specific fields
  const [missions, setMissions] = useState(summary?.missions || '')
  const [successMetrics, setSuccessMetrics] = useState(summary?.successMetrics || '')
  const [links, setLinks] = useState(summary?.links || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setRag(summary?.rag || '')
    setHighlights(summary?.highlights || '')
    setLowlights(summary?.lowlights || '')
    setMissions(summary?.missions || '')
    setSuccessMetrics(summary?.successMetrics || '')
    setLinks(summary?.links || '')
  }, [summary])

  const hasSummary = isW0
    ? (missions || successMetrics || links)
    : (rag || highlights || lowlights)

  const handleSave = async () => {
    setSaving(true)
    if (isW0) {
      await onSave(week, { missions, successMetrics, links })
    } else {
      await onSave(week, { rag, highlights, lowlights })
    }
    setSaving(false)
    setEditing(false)
  }

  if (!isEditable && !hasSummary) return null

  const ragStyle = RAG_COLORS[rag] || {}

  // ---- W0: Cycle Planning Card ----
  if (isW0) {
    if (editing) {
      return (
        <div className="weekly-summary-card editing" style={{ borderLeft: '4px solid #6366F1' }}>
          <div className="weekly-summary-header">
            <span style={{ fontWeight: 600 }}>🎯 Cycle Planning</span>
          </div>
          <div className="weekly-summary-form">
            <div className="summary-field">
              <label>🏔️ Missions</label>
              <textarea value={missions} onChange={e => setMissions(e.target.value)} placeholder="Key missions for this cycle..." rows={3} className="edit-textarea" />
            </div>
            <div className="summary-field">
              <label>📏 Success Metrics</label>
              <textarea value={successMetrics} onChange={e => setSuccessMetrics(e.target.value)} placeholder="How we measure success..." rows={3} className="edit-textarea" />
            </div>
            <div className="summary-field">
              <label>🔗 Links</label>
              <textarea value={links} onChange={e => setLinks(e.target.value)} placeholder="Relevant docs, specs, PRDs... (one per line)" rows={3} className="edit-textarea" />
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button onClick={handleSave} disabled={saving} className="save-btn">{saving ? 'Saving...' : 'Save'}</button>
              <button onClick={() => setEditing(false)} className="cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      )
    }

    if (!hasSummary) {
      return (
        <div className="weekly-summary-card placeholder" onClick={() => setEditing(true)}>
          <span style={{ color: 'var(--text-tertiary)', cursor: 'pointer' }}>🎯 Click to add cycle planning (missions, success metrics, links)...</span>
        </div>
      )
    }

    const renderLinks = (text) => {
      if (!text) return null
      return text.split('\n').filter(l => l.trim()).map((line, i) => {
        const trimmed = line.trim()
        const isUrl = trimmed.startsWith('http://') || trimmed.startsWith('https://')
        return <div key={i} style={{ marginBottom: '4px' }}>{isUrl ? <a href={trimmed} target="_blank" rel="noopener noreferrer" style={{ color: '#6366F1', textDecoration: 'none' }}>{trimmed}</a> : trimmed}</div>
      })
    }

    return (
      <div className="weekly-summary-card" style={{ borderLeft: '4px solid #6366F1', cursor: 'pointer' }} onClick={() => isEditable && setEditing(true)}>
        <div className="weekly-summary-header">
          <span style={{ fontWeight: 600 }}>🎯 Cycle Planning</span>
          {isEditable && <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>click to edit</span>}
        </div>
        {missions && <div className="weekly-summary-text"><span className="summary-label">🏔️ Missions:</span> {missions}</div>}
        {successMetrics && <div className="weekly-summary-text"><span className="summary-label">📏 Success Metrics:</span> {successMetrics}</div>}
        {links && <div className="weekly-summary-text"><span className="summary-label">🔗 Links:</span><div style={{ marginTop: '4px' }}>{renderLinks(links)}</div></div>}
      </div>
    )
  }

  // ---- W1-W8: Regular Weekly Summary ----

  if (editing) {
    return (
      <div className="weekly-summary-card editing" style={{ borderLeft: rag ? `4px solid ${ragStyle.border}` : undefined }}>
        <div className="weekly-summary-header">
          <span style={{ fontWeight: 600 }}>📝 Weekly Summary</span>
        </div>
        <div className="weekly-summary-form">
          <div className="summary-field">
            <label>RAG Status</label>
            <select value={rag} onChange={e => setRag(e.target.value)} className="edit-select">
              {RAG_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="summary-field">
            <label>✅ Highlights</label>
            <textarea value={highlights} onChange={e => setHighlights(e.target.value)} placeholder="Key accomplishments this week..." rows={3} className="edit-textarea" />
          </div>
          <div className="summary-field">
            <label>⚠️ Lowlights</label>
            <textarea value={lowlights} onChange={e => setLowlights(e.target.value)} placeholder="Challenges, blockers, risks..." rows={3} className="edit-textarea" />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button onClick={handleSave} disabled={saving} className="save-btn">{saving ? 'Saving...' : 'Save'}</button>
            <button onClick={() => setEditing(false)} className="cancel-btn">Cancel</button>
          </div>
        </div>
      </div>
    )
  }

  if (!hasSummary) {
    return (
      <div className="weekly-summary-card placeholder" onClick={() => setEditing(true)}>
        <span style={{ color: 'var(--text-tertiary)', cursor: 'pointer' }}>📝 Click to add weekly summary (RAG status, highlights, lowlights)...</span>
      </div>
    )
  }

  return (
    <div className="weekly-summary-card" style={{ borderLeft: rag ? `4px solid ${ragStyle.border}` : undefined, background: ragStyle.bg || 'var(--surface)' }} onClick={() => isEditable && setEditing(true)}>
      <div className="weekly-summary-header">
        <span>{ragStyle.dot || '📝'} <strong>Weekly Summary</strong>{rag && ` — ${RAG_OPTIONS.find(o => o.value === rag)?.label.split('—')[1]?.trim()}`}</span>
        {isEditable && <span className="edit-hint">✏️</span>}
      </div>
      {highlights && <div className="weekly-summary-text"><span className="summary-label">Highlights:</span> {highlights}</div>}
      {lowlights && <div className="weekly-summary-text"><span className="summary-label">Lowlights:</span> {lowlights}</div>}
    </div>
  )
}

export default function WeekOverWeek() {
  const [items, setItems] = useState([])
  const [summaries, setSummaries] = useState({})
  const [loading, setLoading] = useState(true)
  const [selectedWeek, setSelectedWeek] = useState(null)
  const [editingItem, setEditingItem] = useState(null) // { id, field }
  const [editValue, setEditValue] = useState('')

  const currentWeek = useMemo(() => getCurrentWeek(), [])

  useEffect(() => { setSelectedWeek(Math.max(0, currentWeek)) }, [currentWeek])

  useEffect(() => {
    const load = async () => {
      try {
        const workItems = await api.getWorkItems()
        setItems(workItems)
      } catch (e) { console.error(e) }
      try {
        const sums = await api.getWeeklySummaries()
        const sumMap = {}
        ;(sums || []).forEach(s => { sumMap[s.week] = s })
        setSummaries(sumMap)
      } catch (e) { console.error('weekly summaries not available:', e) }
      setLoading(false)
    }
    load()
  }, [])

  const handleSaveSummary = useCallback(async (week, body) => {
    const result = await api.updateWeeklySummary(week, body)
    setSummaries(prev => ({ ...prev, [week]: result }))
  }, [])

  const handleSaveItemField = useCallback(async (item, field) => {
    const newValue = editValue.trim()
    setEditingItem(null)
    if (newValue === (item[field] || '')) return // no change
    try {
      await api.updateWorkItem(item.id, { [field]: newValue })
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, [field]: newValue, updatedAt: new Date().toISOString() } : i))
    } catch (e) {
      console.error('Failed to save', e)
    }
  }, [editValue])

  const weeklyData = useMemo(() => {
    const weeks = Array.from({ length: TOTAL_WEEKS }, (_, w) => {
      const { start, end } = getWeekDates(w)
      const endOfWeek = new Date(end); endOfWeek.setHours(23, 59, 59, 999)
      const weekLabel = `W${w}`

      // Items assigned to this week by flightWeek
      const flightItems = items.filter(item => {
        if (!item.flightWeek) return false
        return item.flightWeek === weekLabel || (w === 0 && item.flightWeek === 'W0')
      })

      // Items updated during this week (but assigned to a different flightWeek)
      const activeItems = items.filter(item => {
        if (!item.updatedAt) return false
        if (flightItems.includes(item)) return false  // already shown
        if (!item.flightWeek || item.flightWeek === weekLabel) return false  // already matched or would match
        return isInWeekRange(item.updatedAt, w)
      })

      const allItems = [...flightItems, ...activeItems]

      const highlights = allItems.filter(i => i.highlights && i.highlights.trim() !== '')
      const lowlights = allItems.filter(i => !i.highlights && i.lowlights && i.lowlights.trim() !== '')
      const inProgress = allItems.filter(i => !highlights.includes(i) && !lowlights.includes(i))

      return { index: w, label: WEEK_LABELS[w], start, end: endOfWeek, items: allItems, flightItems, activeItems, highlights, lowlights, inProgress }
    })

    // Unscheduled tab
    const unscheduled = items.filter(i => !i.flightWeek)
    const uHighlights = unscheduled.filter(i => i.highlights && i.highlights.trim() !== '')
    const uLowlights = unscheduled.filter(i => !i.highlights && i.lowlights && i.lowlights.trim() !== '')
    const uInProgress = unscheduled.filter(i => !uHighlights.includes(i) && !uLowlights.includes(i))
    const { start: uStart, end: uEnd } = getWeekDates(0)
    weeks.push({
      index: TOTAL_WEEKS,
      label: 'Unscheduled',
      start: uStart,
      end: uEnd,
      items: unscheduled,
      flightItems: unscheduled,
      activeItems: [],
      highlights: uHighlights,
      lowlights: uLowlights,
      inProgress: uInProgress,
    })

    return weeks
  }, [items])

  if (loading) return <div className="loading">Loading week over week...</div>

  const selected = weeklyData[selectedWeek] || weeklyData[0]
  const weekKey = selected.index < TOTAL_WEEKS ? `W${selected.index}` : 'Unscheduled'
  const showSummary = selected.index <= currentWeek && selected.index < TOTAL_WEEKS

  const renderSection = (title, icon, sectionItems, className) => {
    if (!sectionItems.length) return null
    return (
      <div className={`wow-section ${className}`}>
        <div className="wow-section-header">
          <span>{icon} {title}</span>
          <span className="wow-section-count">{sectionItems.length}</span>
        </div>
        {sectionItems.map(item => {
          const isActive = selected.activeItems?.includes(item)
          const isEditingHL = editingItem?.id === item.id && editingItem?.field === 'highlights'
          const isEditingLL = editingItem?.id === item.id && editingItem?.field === 'lowlights'
          return (
            <div key={item.id} className={`week-item ${isActive ? 'week-item-active' : ''}`}>
              <span className="week-item-icon">{statusEmoji[item.status] || '📌'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span className="week-item-title">{item.title}</span>
                  <span className={`badge-sm priority-${item.priority?.toLowerCase()}`}>{item.priority}</span>
                  <span className="week-item-status">{item.status}</span>
                  {isActive && <span className="badge-sm badge-active-week" title={`Flight: ${item.flightWeek}, updated this week`}>↻ {item.flightWeek}</span>}
                  {isActive && item.updatedAt && <span className="week-item-updated">{new Date(item.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                </div>
                {/* Highlights - click to edit */}
                {isEditingHL ? (
                  <div className="wow-item-edit">
                    <input
                      autoFocus
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={() => handleSaveItemField(item, 'highlights')}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveItemField(item, 'highlights'); if (e.key === 'Escape') setEditingItem(null); }}
                      placeholder="Key accomplishments..."
                      className="edit-input"
                      style={{ width: '100%', marginTop: '4px' }}
                    />
                  </div>
                ) : (
                  <div
                    className="wow-item-note editable-note"
                    onClick={() => { setEditingItem({ id: item.id, field: 'highlights' }); setEditValue(item.highlights || ''); }}
                    title="Click to edit highlights"
                  >
                    {item.highlights ? `💡 ${item.highlights}` : <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>💡 + Add highlights</span>}
                  </div>
                )}
                {/* Lowlights - click to edit */}
                {isEditingLL ? (
                  <div className="wow-item-edit">
                    <input
                      autoFocus
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={() => handleSaveItemField(item, 'lowlights')}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveItemField(item, 'lowlights'); if (e.key === 'Escape') setEditingItem(null); }}
                      placeholder="Challenges, blockers..."
                      className="edit-input"
                      style={{ width: '100%', marginTop: '4px' }}
                    />
                  </div>
                ) : (
                  <div
                    className="wow-item-note editable-note"
                    onClick={() => { setEditingItem({ id: item.id, field: 'lowlights' }); setEditValue(item.lowlights || ''); }}
                    title="Click to edit lowlights"
                  >
                    {item.lowlights ? `⚠️ ${item.lowlights}` : <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>⚠️ + Add lowlights</span>}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="week-over-week-page fade-in">
      <div className="cycle-header">
        <div>
          <h2 className="cycle-title">Week Over Week</h2>
          <div className="cycle-subtitle">C2: March 9 – May 15, 2026</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LastUpdated data={items} />
          <ExportButton data={items} filename="week-over-week" tabType="week-over-week" />
        </div>
      </div>

      <div className="week-timeline">
        {weeklyData.map((week, i) => (
          <div key={i} className="week-dot-wrapper">
            {i > 0 && <div className={`week-connector ${i <= currentWeek ? 'past' : ''}`} />}
            <div className="week-dot-container">
              <div
                className={`week-dot ${i === 0 ? 'week-planning' : ''} ${i === TOTAL_WEEKS ? 'week-unscheduled' : ''} ${i < currentWeek ? 'past' : i === currentWeek ? 'current' : 'future'} ${selectedWeek === i ? 'current' : ''}`}
                onClick={() => setSelectedWeek(i)}
              >
                {i === TOTAL_WEEKS ? '?' : i}
              </div>
              <div className="week-dot-label">{week.label}{week.items.length > 0 ? ` (${week.items.length})` : ''}</div>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className={`week-card fade-in ${selectedWeek === 0 ? 'week-planning' : ''}`} key={selectedWeek}>
          <div className="week-card-header">
            <div className="week-card-title">📅 {selected.label}{selected.index < TOTAL_WEEKS ? `: ${formatDate(selected.start)} – ${formatDate(selected.end)}` : ` (${selected.items.length} items)`}</div>
            <div className="week-card-dates">
              {selectedWeek === currentWeek && <span className="badge status-active">Current Week</span>}
            </div>
          </div>

          {showSummary && (
            <WeeklySummaryCard
              week={weekKey}
              summary={summaries[weekKey]}
              onSave={handleSaveSummary}
              isEditable={selected.index <= currentWeek}
              isW0={selected.index === 0}
            />
          )}

          {selected.items.length === 0 && !showSummary ? (
            <div className="week-no-data">No data yet for this week</div>
          ) : selected.items.length > 0 && (
            <>
              {renderSection('Highlights', '✅', selected.highlights, 'wow-highlights')}
              {renderSection('Lowlights', '⚠️', selected.lowlights, 'wow-lowlights')}
              {renderSection(selectedWeek > currentWeek ? 'Planned' : 'In Progress', selectedWeek > currentWeek ? '📋' : '🔄', selected.inProgress, selectedWeek > currentWeek ? 'wow-planned' : 'wow-inprogress')}

              <div className="week-stats">
                <div className="week-stat">
                  <div className="week-stat-dot" style={{ background: 'var(--status-active)' }} />
                  Highlights: {selected.highlights.length}
                </div>
                <div className="week-stat">
                  <div className="week-stat-dot" style={{ background: 'var(--status-blocked)' }} />
                  Lowlights: {selected.lowlights.length}
                </div>
                <div className="week-stat">
                  <div className="week-stat-dot" style={{ background: 'var(--status-progress)' }} />
                  {selectedWeek > currentWeek ? 'Planned' : 'In Progress'}: {selected.inProgress.length}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
